import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Plus, Trash2, Bell, BellOff, Eye, Car } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate SEO-friendly slug from vehicle data (brand-model-year)
const generateVehicleSlug = (v) => {
  if (!v) return '';
  const brand = (v.brand || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const model = (v.model || '').toLowerCase().replace(/[^a-z0-9]/g, '').replace(new RegExp(`^${brand}`, 'i'), '');
  const year = v.year || '';
  return `${brand}-${model}-${year}`;
};

const SavedSearches = ({ lang }) => {
  const { t } = useTranslation(lang);
  const navigate = useNavigate();
  const [savedSearches, setSavedSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [matches, setMatches] = useState({});
  const [loadingMatches, setLoadingMatches] = useState({});

  useEffect(() => {
    fetchSavedSearches();
  }, []);

  const fetchSavedSearches = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API}/saved-searches/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSavedSearches(response.data);
    } catch (error) {
      console.error('Error fetching saved searches:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Failed to load saved searches');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCurrentSearch = () => {
    // Get current filters from URL or localStorage
    const currentFilters = JSON.parse(localStorage.getItem('currentSearchFilters') || '{}');
    
    if (Object.keys(currentFilters).length === 0) {
      toast.error('No active search to save. Please search for vehicles first.');
      return;
    }

    setShowCreateDialog(true);
  };

  const handleCreateSearch = async () => {
    if (!searchName.trim()) {
      toast.error('Please enter a name for this search');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const currentFilters = JSON.parse(localStorage.getItem('currentSearchFilters') || '{}');

      await axios.post(`${API}/saved-searches/`, {
        name: searchName,
        filters: currentFilters,
        email_notifications: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Search saved successfully!');
      setShowCreateDialog(false);
      setSearchName('');
      fetchSavedSearches();
    } catch (error) {
      console.error('Error saving search:', error);
      toast.error('Failed to save search');
    }
  };

  const handleDeleteSearch = async (searchId) => {
    if (!window.confirm('Delete this saved search?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/saved-searches/${searchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Search deleted');
      fetchSavedSearches();
    } catch (error) {
      console.error('Error deleting search:', error);
      toast.error('Failed to delete search');
    }
  };

  const handleToggleNotifications = async (searchId, currentValue) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/saved-searches/${searchId}`, {
        email_notifications: !currentValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Notifications ${!currentValue ? 'enabled' : 'disabled'}`);
      fetchSavedSearches();
    } catch (error) {
      console.error('Error updating notifications:', error);
      toast.error('Failed to update notifications');
    }
  };

  const handleViewMatches = async (searchId) => {
    setLoadingMatches(prev => ({ ...prev, [searchId]: true }));

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/saved-searches/${searchId}/matches`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMatches(prev => ({ ...prev, [searchId]: response.data }));
    } catch (error) {
      console.error('Error fetching matches:', error);
      toast.error('Failed to load matches');
    } finally {
      setLoadingMatches(prev => ({ ...prev, [searchId]: false }));
    }
  };

  const formatFilters = (filters) => {
    const parts = [];
    
    if (filters.brand) parts.push(`Brand: ${filters.brand}`);
    if (filters.model) parts.push(`Model: ${filters.model}`);
    if (filters.price_min || filters.price_max) {
      const priceRange = `$${filters.price_min || '0'} - $${filters.price_max || '∞'}`;
      parts.push(`Price: ${priceRange}`);
    }
    if (filters.year_min || filters.year_max) {
      const yearRange = `${filters.year_min || '1900'} - ${filters.year_max || new Date().getFullYear()}`;
      parts.push(`Year: ${yearRange}`);
    }
    if (filters.mileage_max) parts.push(`Max Mileage: ${filters.mileage_max}`);
    if (filters.transmission) parts.push(`${filters.transmission}`);
    if (filters.fuel_type) parts.push(`${filters.fuel_type}`);

    return parts.length > 0 ? parts.join(' • ') : 'No filters';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-secondary)' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--accent-primary)' }}></div>
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title="Saved Searches | AAA of Miami"
        description="Manage your saved vehicle searches and notifications"
      />
      <div className="min-h-screen pt-20 pb-12" style={{ background: 'var(--bg-secondary)' }}>
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
              Saved Searches
            </h1>
            <p style={{ color: 'var(--text-secondary)' }}>
              Manage your saved searches and get notified when new matching vehicles arrive
            </p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button onClick={handleSaveCurrentSearch}>
                <Plus className="w-4 h-4 mr-2" />
                Save Current Search
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Save Search</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Search Name</label>
                  <Input
                    placeholder="e.g., 'Luxury SUVs under $50k'"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                  />
                </div>
                <Button className="w-full" onClick={handleCreateSearch}>
                  Save Search
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Saved Searches List */}
        {savedSearches.length === 0 ? (
          <div className="text-center py-16 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
            <Search className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-secondary)', opacity: 0.3 }} />
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              No Saved Searches Yet
            </h3>
            <p style={{ color: 'var(--text-secondary)' }} className="mb-4">
              Search for vehicles and save your preferences to get notified
            </p>
            <Link to="/inventory">
              <Button>Browse Inventory</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {savedSearches.map((search) => (
              <div key={search.id} className="rounded-lg p-6" style={{ background: 'var(--bg-primary)' }}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                      {search.name}
                    </h3>
                    <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                      {formatFilters(search.filters)}
                    </p>
                    <div className="flex items-center gap-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <span>Created: {new Date(search.created_at).toLocaleDateString()}</span>
                      {search.notification_count > 0 && (
                        <span>• {search.notification_count} notifications sent</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewMatches(search.id)}
                      disabled={loadingMatches[search.id]}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      {loadingMatches[search.id] ? 'Loading...' : 'View Matches'}
                    </Button>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={search.email_notifications}
                        onCheckedChange={() => handleToggleNotifications(search.id, search.email_notifications)}
                      />
                      {search.email_notifications ? (
                        <Bell className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
                      ) : (
                        <BellOff className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteSearch(search.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                {/* Matches */}
                {matches[search.id] && (
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <Car className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                      <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {matches[search.id].match_count} Matching Vehicles
                      </span>
                    </div>
                    {matches[search.id].match_count > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {matches[search.id].vehicles.slice(0, 6).map((vehicle) => (
                          <Link
                            key={vehicle.id}
                            to={`/${generateVehicleSlug(vehicle)}`}
                            className="border rounded-lg overflow-hidden hover:shadow-lg transition"
                            style={{ borderColor: 'var(--border-color)' }}
                          >
                            {vehicle.images?.[0] && (
                              <img
                                src={vehicle.images[0]}
                                alt={vehicle.title}
                                className="w-full h-32 object-cover"
                              />
                            )}
                            <div className="p-3">
                              <p className="font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>
                                {vehicle.title || `${vehicle.year} ${vehicle.brand} ${vehicle.model}`}
                              </p>
                              <p className="text-sm font-bold" style={{ color: 'var(--accent-primary)' }}>
                                ${vehicle.price?.toLocaleString()}
                              </p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: 'var(--text-secondary)' }}>No matching vehicles at the moment</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default SavedSearches;
